import { forwardRef, memo, ReactNode, useCallback, useMemo, useState } from 'react'
import { ItemProps, TableProps, TableVirtuoso } from 'react-virtuoso'
import { BoxTruckBidder, BoxTruckLoadAnnotation, BoxTruckOperatorBid, LoadBid } from '@numeo/types'
import { CanonicalLoad } from '@numeo/types-core'
import { FilterIndicator } from 'components/loads/LoadsTable'
import { compareSortValues, formatRateUsd, formatRpmUsd, loadSortValue } from 'components/loads/loadMetrics'
import { arrivedAt, fmtMiles, fmtReceivedAt, receivedAtTitle } from '../utils/format'
import type { SortColumn, SortDir } from 'components/loads/types'
import { AgeDisplay } from 'components/shared/LoadsTable/LoadRow/AgeDisplay'
import { bestBid, bidderLabel, bidsCellLabel, dispatcherLabel, fmtBidPrice } from '../utils/bidFormat'
import { BoxTruckEmailThread } from '../hooks/useBoxTruckOverlays'
import { loadKey, localityWithZip, numeoId, sourceIconKey, sourceLabel, vehicleLabel } from '../utils/loadFields'
import { useLatestRef } from 'hooks/useLatestRef'
import { useViewedBoxTruckLoads } from '../hooks/useViewedBoxTruckLoads'
import { useScrollGatedPagination, VIRTUOSO_OVERSCAN_PX } from '../hooks/useScrollGatedPagination'
import { useKeepBoardAtTop } from '../hooks/useKeepBoardAtTop'
import { useBoxTruckTableScroll } from '../hooks/useBoxTruckTableScroll'
import { BoxTruckContactCell } from './BoxTruckContactCell'
import { CompactStarRating } from './StarRating'
import { BoxTruckSourceIcon } from './BoxTruckSourceIcon'

interface BoxTruckTableProps {
    loads: CanonicalLoad[]
    selectedKey: string | null
    onRowClick: (load: CanonicalLoad) => void
    /** Middle-click (mouse scroll button) a row → open that load in a new browser tab. Absent → no-op. */
    onOpenNewTab?: (load: CanonicalLoad) => void
    /** Driver/carrier bids keyed by raw externalId (loadKey). Absent key → no bids on that load; omitted
     *  entirely (e.g. the email-thread Bidding page) → the Bids column stays empty. */
    bids?: Record<string, LoadBid[]>
    /** Bid-email activity from dispatch operators, keyed by load id. */
    operatorBids?: Record<string, BoxTruckOperatorBid[]>
    /** Who on the team bid on each load, keyed by raw externalId — names where the Bidding page resolved
     *  them. Absent → the Bid by column falls back to the operator-bid emails. */
    bidders?: Record<string, BoxTruckBidder[]>
    /** The team's shared rating + note per load, keyed by raw externalId. Drives the Rating column. */
    annotations?: Record<string, BoxTruckLoadAnnotation>
    /** Broker email-thread state per load (thread id + unread replies), keyed by raw externalId — flips the
     *  Contact cell's compose icon to the green thread icon with the unread badge, like Load Hub. */
    emailThreads?: Record<string, BoxTruckEmailThread>
    /** Loads whose driver bid this desk has not emailed a broker about (ADR-057). The Bids cell says so,
     *  and the server has already put them first. Omitted on a board where the question does not apply. */
    awaitingDesk?: ReadonlySet<string>
    /** Set the team rating on a load (inline star click). Omit → the Rating column is read-only. */
    onRate?: (load: CanonicalLoad, rating: number) => void
    currentOperatorId?: string
    /** Fetch the next page as the operator nears the bottom (infinite scroll). */
    onEndReached?: () => void
    /** Fires with `true` while the board is within AT_TOP_THRESHOLD_PX of the top — drives auto-refresh gating. */
    /** Imperative handle so the page can jump the virtualized list back to the top on a new top load. */
    /** Which columns to render. Omitted → every column, so a caller that doesn't offer the picker (the
     *  email-thread Bidding page) is unaffected. */
    visibleColumns?: Record<BoxTruckColumnId, boolean>
    /** Display order for the columns. Omitted by surfaces without the column customizer. */
    columnOrder?: BoxTruckColumnId[]
    /** Search-tab/board identity used to isolate the virtual scroller's vertical position. */
    scrollKey?: string
    readScrollTop?: (key: string) => number
    writeScrollTop?: (key: string, scrollTop: number) => void
}

/** Per-row styling (selection, teammate/own bid tint, zebra) + the row click need these, but TableVirtuoso only
 *  hands the custom TableRow the row `item` and one shared `context` — so the rest rides along on the context. */
/**
 * Sort by a key read ONCE per load, not once per comparison.
 *
 * Every ranking on this board comes from parsing a timestamp or coercing a details field, and the board
 * re-sorts on each array identity change — which on a live feed is EVERY delivered load, over up to
 * `MAX_BOARD_LOADS` rows. A comparator that extracts as it compares therefore pays ~2 n log n extractions
 * (≈44k `Date.parse` calls on a full board) to produce n distinct answers. Decorating first pays n.
 *
 * Stable, like the comparator form it replaced: `Array.prototype.sort` is stable and the decorate/undecorate
 * round trip preserves order, so equal keys keep their input order exactly as they did through a comparator
 * returning 0. The null rule is {@link compareSortValues}, shared with the load-hub table's `compareLoadsBy`
 * so the two cannot disagree about where a load with no value goes.
 */
const sortByNumericKey = (loads: CanonicalLoad[], keyOf: (load: CanonicalLoad) => number | null, dir: 'asc' | 'desc'): CanonicalLoad[] => {
    const decorated = loads.map((load) => ({ load, key: keyOf(load) }))
    decorated.sort((a, b) => compareSortValues(a.key, b.key, dir))
    return decorated.map((entry) => entry.load)
}

/**
 * The board's order when the operator has not picked a column: whatever reached us most recently, first.
 *
 * Exported so the direction is pinned by a test rather than by reading it back off the screen. Getting it
 * backwards is a one-character mistake that shows up as the oldest loads sitting at the top of a live feed
 * — which is exactly the regression this replaced. The board calls THIS, so the test and the screen cannot
 * be describing different orders.
 */
export const sortByNewestFirst = (loads: CanonicalLoad[]): CanonicalLoad[] => sortByNumericKey(loads, arrivedAt, 'desc')

interface RowContext {
    selectedKey: string | null
    currentOperatorId?: string
    operatorBids: Record<string, BoxTruckOperatorBid[]>
    /** Loads the operator has already opened this session (drawer or new tab) — rendered dimmed, the
     *  load-hub viewed treatment, so checked loads are distinguishable without having bid on them. */
    viewedKeys: Set<string>
    /** Driver/carrier bids keyed by loadKey — a load with any drives the soft-red row tint. */
    bids: Record<string, LoadBid[]>
    onRowClick: (load: CanonicalLoad) => void
    onOpenNewTab?: (load: CanonicalLoad) => void
    /** Scales with the visible column count — the table shell reads it off the shared context. */
    minTableWidth: number
}

/** A middle-click that lands on an interactive control (the star rating, a link) is a row ACTION, not a
 *  request to open the load — mirror how left-click ignores those (StarRating stops propagation). */
const isRowActionTarget = (target: EventTarget | null): boolean => !!(target as HTMLElement | null)?.closest('button, a, input, select, [role="button"]')

// A row is "near the top" (auto-refresh allowed) within this many px — mirrors the old scrollTop > 300 guard.
const AT_TOP_THRESHOLD_PX = 300

const milesValue = (l: CanonicalLoad): number | null => (l.distanceMiles == null || !Number.isFinite(l.distanceMiles) ? null : l.distanceMiles)

/** Box-truck sortable columns: the shared load-hub set (age/rate/rpm/dhO/dhD) plus Miles (box-truck-only). */
// 'miles' and 'received' are board-local: neither is in the shared load-hub comparator, and `received` in
// particular must NOT go there — arrival is a Sylectus-feed concept this board owns.
type BtSortCol = SortColumn | 'miles' | 'received'

/**
 * Shared empties for a load that has no bids / no bidders / no outreach — the overwhelming majority of rows.
 *
 * `overlay[key] ?? []` mints a fresh array per row per render, which is enough on its own to fail the
 * memoized row's prop comparison for exactly the common case and re-render every visible cell. Same for the
 * `= {}` prop defaults: a caller that omits an overlay (the Bidding page omits three) would hand this
 * component a different object on every one of its own renders.
 */
const NO_BIDS: LoadBid[] = []
const NO_BIDDERS: BoxTruckBidder[] = []
const NO_OPERATOR_BIDS: BoxTruckOperatorBid[] = []
const NO_BIDS_BY_LOAD: Record<string, LoadBid[]> = {}
const NO_BIDDERS_BY_LOAD: Record<string, BoxTruckBidder[]> = {}
const NO_OPERATOR_BIDS_BY_LOAD: Record<string, BoxTruckOperatorBid[]> = {}
const NO_ANNOTATIONS: Record<string, BoxTruckLoadAnnotation> = {}
const NO_EMAIL_THREADS: Record<string, BoxTruckEmailThread> = {}
const NOBODY_WAITING: ReadonlySet<string> = new Set()

/** Everything a cell can need beyond the load itself — the per-load overlays and the row actions. */
interface CellContext {
    bids: LoadBid[]
    /** This load's driver bid is still waiting for someone here to email the broker. */
    awaitingDesk: boolean
    bidders: BoxTruckBidder[]
    operatorBids: BoxTruckOperatorBid[]
    annotation?: BoxTruckLoadAnnotation
    thread?: BoxTruckEmailThread
    onRate?: (load: CanonicalLoad, rating: number) => void
    onOpen: (load: CanonicalLoad) => void
}

export type BoxTruckColumnId =
    | 'posted'
    | 'age'
    | 'numeoId'
    | 'source'
    | 'rate'
    | 'rpm'
    | 'miles'
    | 'pickup'
    | 'dhO'
    | 'delivery'
    | 'dhD'
    | 'vehicle'
    | 'rating'
    | 'bids'
    | 'bidder'
    | 'contact'
    | 'brokerage'

interface BoxTruckColumn {
    id: BoxTruckColumnId
    label: string
    /** Horizontal alignment shared by this column's header and body cells. */
    align?: 'left' | 'center'
    /** Marks the sortable ones (reusing the load-hub FilterIndicator + comparator). */
    sort?: BtSortCol
    /** Short glanceable values get half the horizontal padding so the board fits smaller screens. */
    narrow?: boolean
    /** Extra classes for the cell, on top of the shared padding. */
    cellClass?: string
    render: (load: CanonicalLoad, ctx: CellContext) => ReactNode
}

/**
 * The board's columns, in order. Mirrors the load-hub grouping: money (Rate/RPM/Miles), then the
 * origin/DH-O/destination/DH-D lane, then the box-truck extras.
 *
 * Header and body BOTH derive from this one list. They used to be a `.map` over labels and fifteen
 * hand-written `<td>`s that happened to be in the same order — which held only as long as nobody edited one
 * side, and made hiding a column a two-place change with no way to notice a mismatch. Now a column is one
 * entry, and a hidden column disappears from both by construction.
 */
const COLUMNS: BoxTruckColumn[] = [
    {
        // The id is the layout STORAGE KEY and stays as it is — renaming it would drop every operator's saved
        // show/hide and column order for a change that is purely presentational.
        id: 'posted',
        label: 'Received',
        // The same value the default order uses, so clicking this header re-sorts into the sequence the board
        // already had rather than shuffling it into a different one. It used to reuse the Age comparator,
        // which reads createdAt — and that was the bug: the column showed one clock while the board was
        // ordered by another, so a load posted LATER could sit below one posted earlier and look mis-sorted.
        sort: 'received',
        narrow: true,
        cellClass: 'whitespace-nowrap tabular-nums text-foreground-muted',
        // When the load reached US, as a clock time in the operator's timezone. Relative age reads well on a
        // board you watch continuously, but it costs a subtraction every time you want to know WHEN something
        // landed — which is what an operator compares against a driver's hours and a pickup. The broker's own
        // claimed posted time is one hover away, in the tooltip.
        render: (load) => <span title={receivedAtTitle(load)}>{fmtReceivedAt(load)}</span>,
    },
    {
        id: 'age',
        label: 'Age',
        sort: 'age',
        narrow: true,
        cellClass: 'whitespace-nowrap text-foreground-muted',
        // Same age display + format as Load Hub for EVERY source: relative age (Xm/Xh/Xd), auto-updating
        // every minute. Sylectus used to show its exact ET clock time instead, which read as a different
        // scale next to DAT's relative ages on the now-chronological board. Reads createdAt — consistent
        // with the Age sort.
        render: (load) => (
            <AgeDisplay postedAt={load.createdAt ? (typeof load.createdAt === 'string' ? load.createdAt : load.createdAt.toISOString()) : null} />
        ),
    },
    {
        id: 'numeoId',
        label: 'Numeo ID',
        narrow: true,
        cellClass: 'whitespace-nowrap',
        render: (load) => <span className="font-mono font-semibold text-foreground">{numeoId(load) ?? '—'}</span>,
    },
    {
        id: 'source',
        label: 'Source',
        align: 'center',
        narrow: true,
        cellClass: 'whitespace-nowrap',
        render: (load) => {
            const sourceKey = sourceIconKey(load)
            return (
                <span className="flex w-full items-center justify-center" title={sourceLabel(load)}>
                    <BoxTruckSourceIcon
                        sourceKey={sourceKey}
                        fallback={<span className="text-xs font-medium text-foreground-muted">{sourceLabel(load)}</span>}
                    />
                </span>
            )
        },
    },
    {
        id: 'rate',
        label: 'Rate',
        sort: 'rate',
        cellClass: 'whitespace-nowrap font-medium tabular-nums text-foreground',
        render: (load) => formatRateUsd(load) ?? '—',
    },
    {
        id: 'rpm',
        label: 'RPM',
        sort: 'rpm',
        cellClass: 'whitespace-nowrap font-medium tabular-nums text-foreground',
        render: (load) => formatRpmUsd(load) ?? '—',
    },
    {
        id: 'miles',
        label: 'Miles',
        sort: 'miles',
        cellClass: 'whitespace-nowrap font-medium tabular-nums text-foreground',
        render: (load) => fmtMiles(load.distanceMiles),
    },
    {
        id: 'pickup',
        label: 'Pick-up',
        cellClass: 'whitespace-nowrap font-medium text-foreground',
        render: (load) => localityWithZip(load.pickupCity, load.pickupStateProv, load.pickupPostalCode),
    },
    {
        id: 'dhO',
        label: 'DH-O',
        sort: 'dhO',
        cellClass: 'whitespace-nowrap tabular-nums text-foreground-muted',
        render: (load) => fmtMiles(load.details?.origin_deadhead_miles),
    },
    {
        id: 'delivery',
        label: 'Delivery',
        cellClass: 'whitespace-nowrap font-medium text-foreground',
        render: (load) => localityWithZip(load.deliveryCity, load.deliveryStateProv, load.deliveryPostalCode),
    },
    {
        id: 'dhD',
        label: 'DH-D',
        sort: 'dhD',
        cellClass: 'whitespace-nowrap tabular-nums text-foreground-muted',
        render: (load) => fmtMiles(load.details?.destination_deadhead_miles),
    },
    {
        id: 'vehicle',
        label: 'Vehicle',
        cellClass: 'whitespace-nowrap font-semibold uppercase tracking-wide text-foreground',
        render: (load) => vehicleLabel(load),
    },
    {
        id: 'rating',
        label: 'Rating',
        narrow: true,
        cellClass: 'whitespace-nowrap',
        // Collapsed to one star + count badge (a five-star row per line was the widest non-text column) —
        // clicking it opens the full interactive picker in a popover.
        render: (load, ctx) => (
            <CompactStarRating value={ctx.annotation?.rating ?? null} onChange={ctx.onRate ? (rating) => ctx.onRate!(load, rating) : undefined} />
        ),
    },
    {
        id: 'bids',
        label: 'Bids',
        cellClass: 'whitespace-nowrap',
        // A bid nobody here has emailed a broker about wears the accent tone and a dot, and the board has
        // already put those rows first (ADR-057). It is the same chip rather than a column of its own: the
        // question is about the BID, and a dispatcher scanning this board reads one place for it.
        render: (_load, ctx) =>
            ctx.bids.length === 0 ? (
                <span className="text-foreground-muted">—</span>
            ) : (
                <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        ctx.awaitingDesk ? 'bg-accent-soft text-accent-soft-foreground' : 'bg-success-soft text-success-soft-foreground'
                    }`}
                    title={[
                        ...ctx.bids.map((b) => `${bidderLabel(b)} — ${fmtBidPrice(b.price, b.currency)}`),
                        ...(ctx.awaitingDesk ? ['Nobody here has emailed the broker about this one yet'] : []),
                    ].join('\n')}
                >
                    {ctx.awaitingDesk && <span className="size-1.5 rounded-full bg-current" />}
                    {/* WHO, then how much. A dispatcher reading a bid is about to email a broker and then
                        tell a driver, so the driver's name is the first thing the row has to answer — it
                        used to be a tooltip away. The price shown is the best one on the load. */}
                    {bidsCellLabel(ctx.bids)}
                    <span className="opacity-50">·</span>
                    {fmtBidPrice(bestBid(ctx.bids).price, bestBid(ctx.bids).currency)}
                </span>
            ),
    },
    {
        id: 'bidder',
        label: 'Bid by',
        cellClass: 'whitespace-nowrap',
        // Who on THIS team sent the broker a bid. Resolved names when the caller has them (the Bidding
        // page); otherwise the operator-bid emails the loadboard already carries for its teammate tint —
        // so the column says something on both boards instead of being a dead option on one.
        render: (_load, ctx) => <BidderCell bidders={ctx.bidders} operatorBids={ctx.operatorBids} />,
    },
    {
        id: 'contact',
        label: 'Contact',
        cellClass: 'whitespace-nowrap',
        render: (load, ctx) => <BoxTruckContactCell load={load} thread={ctx.thread} onOpen={ctx.onOpen} />,
    },
    {
        id: 'brokerage',
        label: 'Brokerage',
        // Fixed width so every broker cell is the same size regardless of name length. A long name truncates
        // with an ellipsis (max-width on the <td> is ignored in an auto-layout table, so the constraint lives
        // on this inner block); the full name shows in the hover tooltip.
        render: (load) => (
            <div className="w-[200px] truncate text-foreground" title={load.brokerCompanyName ?? undefined}>
                {load.brokerCompanyName || '—'}
            </div>
        ),
    },
]

/**
 * The "Bid by" cell. Prefers the resolved bidder identities the Bidding page supplies; where the caller has
 * none (the loadboard) it falls back to the operator-bid rows it already loads for the teammate tint, so the
 * column is meaningful on both boards. Multiple senders collapse to "First +N", full list in the tooltip.
 */
const BidderCell = ({ bidders, operatorBids }: { bidders: BoxTruckBidder[]; operatorBids: BoxTruckOperatorBid[] }) => {
    const people = bidders.length > 0 ? bidders : operatorBids.map((bid) => ({ id: bid.operatorId, email: bid.operatorEmail ?? undefined }))
    if (people.length === 0) return <span className="text-foreground-muted">—</span>
    const title = people.map((p) => p.email || dispatcherLabel(p)).join('\n')
    return (
        <span className="inline-flex max-w-[160px] items-center gap-1 truncate text-foreground" title={title}>
            <span className="truncate">{dispatcherLabel(people[0])}</span>
            {people.length > 1 && <span className="shrink-0 text-xs text-foreground-muted">+{people.length - 1}</span>}
        </span>
    )
}

/**
 * Off until the operator adds them from Table Settings — the board opens on the lane and what it takes to
 * read a load, and everything else is opt-in.
 *
 * Deliberately separate from "can be hidden": nothing here is locked. An operator who prefers relative Age
 * can turn it on and turn Posted off, which conflating the two would have made impossible. Emptying the
 * table is prevented by the picker's own minVisibleColumns, not by pinning columns.
 */
const HIDDEN_BY_DEFAULT: BoxTruckColumnId[] = ['age', 'numeoId', 'rate', 'rpm', 'dhO', 'dhD', 'bids', 'bidder', 'rating', 'contact']

/** Column id → label, for the settings picker. */
export const BOX_TRUCK_COLUMN_DEFS = COLUMNS.map((c) => ({ id: c.id, label: c.label }))

/** The first-run layout: everything except the opt-in set. */
export const BOX_TRUCK_COLUMN_DEFAULT_VISIBLE = Object.fromEntries(COLUMNS.map((c) => [c.id, !HIDDEN_BY_DEFAULT.includes(c.id)])) as Record<
    BoxTruckColumnId,
    boolean
>

export const BOX_TRUCK_COLUMN_IDS = COLUMNS.map((c) => c.id)

/** Cell padding for the compact columns vs the rest — kept as constants so header and body stay in step. */
const NARROW_PAD = 'px-2'
const WIDE_PAD = 'px-4'

interface RowCellsProps {
    load: CanonicalLoad
    columns: BoxTruckColumn[]
    bids: LoadBid[]
    awaitingDesk: boolean
    bidders: BoxTruckBidder[]
    operatorBids: BoxTruckOperatorBid[]
    annotation?: BoxTruckLoadAnnotation
    thread?: BoxTruckEmailThread
    onRate?: (load: CanonicalLoad, rating: number) => void
    onOpen: (load: CanonicalLoad) => void
}

/**
 * One row's cells, memoized on exactly what they read.
 *
 * The board re-renders for plenty of reasons that have nothing to do with a row's contents: the operator
 * clicks a header, scrolls (Virtuoso reports `isScrolling`), opens the drawer, marks a load read, or a live
 * load arrives and replaces the data array. Rendering the cells inline meant all of that cost ~40 visible
 * rows x up to 17 columns of work — formatters, tooltips, star ratings, contact cells — to produce the same
 * DOM. Everything a cell can read is a prop here, so React skips a row whose inputs are unchanged, and the
 * cost of a repaint scales with what actually moved rather than with the size of the viewport.
 *
 * Deliberately NOT part of the row `<tr>`: selection, the viewed dim and the bid tints live on the row
 * element and change far more often than the cells do. Keeping them on `VirtuosoTableRow` (which renders no
 * cell content) lets a row restyle without re-rendering a single cell.
 */
const BoxTruckRowCells = memo(({ load, columns, bids, awaitingDesk, bidders, operatorBids, annotation, thread, onRate, onOpen }: RowCellsProps) => {
    const ctx: CellContext = { bids, awaitingDesk, bidders, operatorBids, annotation, thread, onRate, onOpen }
    return (
        <>
            {columns.map((col) => (
                <td key={col.id} className={`${col.narrow ? NARROW_PAD : WIDE_PAD} py-2 ${col.align === 'center' ? 'text-center' : ''} ${col.cellClass ?? ''}`}>
                    {col.render(load, ctx)}
                </td>
            ))}
        </>
    )
})
BoxTruckRowCells.displayName = 'BoxTruckRowCells'

// TableVirtuoso slot overrides: a plain table shell, a horizontally-scrollable scroller, and a sticky head so
// the columns stay pinned while the (vertically virtualized) rows scroll under them.
const VirtuosoTable = ({ style, context, ...props }: TableProps & { context?: RowContext }) => (
    <table {...props} style={{ ...style, minWidth: context?.minTableWidth ?? 1320 }} className="w-full border-collapse text-sm" />
)

const VirtuosoScroller = forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ style, ...props }, ref) => (
    <div ref={ref} {...props} style={{ ...style, maxWidth: '100%', overflowX: 'auto', overscrollBehaviorX: 'contain' }} />
))
VirtuosoScroller.displayName = 'VirtuosoScroller'

const VirtuosoTableHead = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(({ style, ...props }, ref) => (
    <thead ref={ref} {...props} style={{ ...style, position: 'sticky', top: 0, zIndex: 20 }} />
))
VirtuosoTableHead.displayName = 'VirtuosoTableHead'

/** Hoisted: a fresh object/closure per render is a prop change to Virtuoso, which has no reason to be one. */
const FILL_HEIGHT = { height: '100%' } as const
const itemKey = (_index: number, load: CanonicalLoad): string => loadKey(load)

const VirtuosoTableRow = ({ item, context, ...props }: ItemProps<CanonicalLoad> & { context: RowContext }) => {
    const key = loadKey(item)
    const selected = key === context.selectedKey
    const viewed = context.viewedKeys.has(key)
    const hasDriverBids = (context.bids[key] ?? []).length > 0
    const operatorBidEmails = context.operatorBids[key] ?? []
    // Teammate activity intentionally wins when both flags are true: preventing duplicate outreach takes
    // priority, while the drawer still shows both actors.
    const bidByTeammate = !!context.currentOperatorId && operatorBidEmails.some((bid) => bid.operatorId !== context.currentOperatorId)
    const bidByCurrentOperator = !!context.currentOperatorId && operatorBidEmails.some((bid) => bid.operatorId === context.currentOperatorId)
    // Zebra by absolute index (data-index) so it stays stable as virtualized rows recycle.
    const zebra = props['data-index'] % 2 === 0 ? 'bg-surface' : 'bg-background'
    const tinted = bidByTeammate || bidByCurrentOperator || hasDriverBids
    return (
        <tr
            {...props}
            onClick={(e) => {
                // Cmd/Ctrl-click opens the load in a new tab too — the browser's standard "open in new tab"
                // affordance, and the only way to trigger it on a Mac trackpad (no middle button). Plain click
                // still opens the in-page drawer. A row action (star / link) is left to handle its own click.
                if ((e.metaKey || e.ctrlKey) && context.onOpenNewTab && !isRowActionTarget(e.target)) {
                    context.onOpenNewTab(item)
                    return
                }
                context.onRowClick(item)
            }}
            onMouseDown={(e) => {
                // Middle button starts the browser's autoscroll — suppress it so middle-click only opens a tab.
                if (e.button === 1) e.preventDefault()
            }}
            onAuxClick={(e) => {
                // Middle-click (button 1) opens the load in a new tab, unless it lands on a row action.
                if (e.button !== 1 || !context.onOpenNewTab || isRowActionTarget(e.target)) return
                context.onOpenNewTab(item)
            }}
            // Viewed rows render at half opacity (except while selected — the open drawer's row stays fully
            // readable), reading "acted on AND already checked". An UNTINTED viewed row also desaturates, which
            // drains the colour out of its source icon and star rating; a tinted one must NOT, because the tints
            // are 15%-alpha fills and `grayscale` turns them into the same neutral as the zebra — erasing the
            // only signal that this load has bids on it. Tint priority: selected > teammate/own broker outreach
            // (green/blue — the duplicate-outreach guard outranks) > has driver bids (soft red, so bid loads pop
            // out of the zebra even mid-scroll) > zebra.
            className={`cursor-pointer border-b border-l-2 border-border border-l-transparent transition-colors ${
                selected
                    ? 'border-l-accent bg-default shadow-inner-ds hover:bg-default-hover'
                    : bidByTeammate
                      ? 'bg-success-soft hover:bg-success-soft-hover'
                      : bidByCurrentOperator
                        ? 'bg-accent-soft hover:bg-accent-soft-hover'
                        : hasDriverBids
                          ? 'bg-danger-soft hover:bg-danger-soft-hover'
                          : `${zebra} hover:bg-default`
            } ${viewed && !selected ? `opacity-50 ${tinted ? '' : 'grayscale'}` : ''}`}
        />
    )
}

export const BoxTruckTable = ({
    loads,
    selectedKey,
    onRowClick,
    onOpenNewTab,
    bids = NO_BIDS_BY_LOAD,
    operatorBids = NO_OPERATOR_BIDS_BY_LOAD,
    bidders = NO_BIDDERS_BY_LOAD,
    annotations = NO_ANNOTATIONS,
    emailThreads = NO_EMAIL_THREADS,
    awaitingDesk = NOBODY_WAITING,
    onRate,
    currentOperatorId,
    onEndReached,
    visibleColumns,
    columnOrder,
    scrollKey,
    readScrollTop,
    writeScrollTop,
}: BoxTruckTableProps) => {
    // Single active sort, like the load-hub table: one column + direction (or none). age/rate/rpm/dhO/dhD reuse
    // the shared comparator; Miles (box-truck-only) sorts locally. Nulls always sort last.
    //
    // `null` means "no column chosen", NOT "no order" — see sortedLoads. No header renders as sorted until
    // the operator picks one, so any column is still one click away in either direction.
    const [sort, setSort] = useState<{ col: BtSortCol; dir: 'asc' | 'desc' } | null>(null)
    const captureScrollPosition = useBoxTruckTableScroll({ scrollKey, readScrollTop, writeScrollTop })

    // Loads the operator has opened (drawer click or new-tab open) — the load-hub "viewed" treatment,
    // PERSISTED per account (localStorage, 7-day TTL) so the dim survives feed refreshes, table
    // remounts, and full page reloads instead of "lighting back up" on the next render pass.
    const { viewedKeys, markViewed: markKeyViewed } = useViewedBoxTruckLoads()
    const markViewed = useCallback((load: CanonicalLoad) => markKeyViewed(loadKey(load)), [markKeyViewed])
    const handleRowClick = useCallback(
        (load: CanonicalLoad) => {
            markViewed(load)
            onRowClick(load)
        },
        [markViewed, onRowClick]
    )
    const handleOpenNewTab = useMemo(
        () =>
            onOpenNewTab
                ? (load: CanonicalLoad) => {
                      markViewed(load)
                      onOpenNewTab(load)
                  }
                : undefined,
        [markViewed, onOpenNewTab]
    )

    // Held in a ref so a caller's inline `onRate={(load, rating) => …}` cannot defeat the row memoization
    // below. It did: the loadboard passed exactly that, so the handler was a new function on every render of
    // the page, every row failed its prop comparison, and the memo bought nothing on the one board it was
    // added for. A table that can be switched off by how its caller spells a prop is not memoized, so the
    // identity is owned here. Undefined when the caller supplies none — the Rating column reads that to
    // decide whether its stars are interactive, so it cannot be papered over with a no-op.
    const onRateRef = useLatestRef(onRate)
    const forwardRate = useCallback((load: CanonicalLoad, rating: number) => onRateRef.current?.(load, rating), [onRateRef])
    const rateHandler = onRate ? forwardRate : undefined

    // Infinite scroll, gated on the operator's scroll rather than on the rows fitting — see the hook.
    const { endReached, isScrolling, atBottomStateChange, captureScroller: capturePaginationScroller } = useScrollGatedPagination(onEndReached)
    // Both hooks want the same element — one to restore and persist this board's scroll position, the other
    // to read where the operator actually is before asking for a page. Virtuoso hands out one `scrollerRef`,
    // so the table is where they meet; neither needs to know about the other.
    const captureScroller = useCallback(
        (node: HTMLElement | Window | null) => {
            captureScrollPosition(node)
            capturePaginationScroller(node)
        },
        [captureScrollPosition, capturePaginationScroller]
    )
    // One filter feeds the header and every row, so the two can never fall out of step.
    const shownColumns = useMemo(() => {
        const orderedColumns = columnOrder
            ? columnOrder.map((id) => COLUMNS.find((column) => column.id === id)).filter((column): column is BoxTruckColumn => !!column)
            : COLUMNS
        return visibleColumns ? orderedColumns.filter((column) => visibleColumns[column.id]) : orderedColumns
    }, [columnOrder, visibleColumns])
    // The min-width exists so the wide default set scrolls rather than crushing; with columns hidden it has
    // to shrink with them, or a trimmed board would stretch its few columns across a needless 1320px.
    const minTableWidth = Math.max(720, Math.round((1320 * shownColumns.length) / COLUMNS.length))

    const dirFor = useCallback((col: BtSortCol): SortDir => (sort?.col === col ? sort.dir : 'none'), [sort])
    const setColSort = useCallback((col: BtSortCol, next: SortDir) => setSort(next === 'none' ? null : { col, dir: next }), [])

    // Zebra striping is applied on this rendered order.
    const sortedLoads = useMemo(() => {
        // With no column chosen the board reads newest-first, because that is the question this board answers:
        // what just came in. Fetch order cannot stand in for it — results arrive as one stream per source and
        // merge through an insertion-ordered Map, so every Sylectus load lands ahead of every DAT one however
        // old it is. Ordering here rather than seeding `sort` keeps the headers unmarked, and puts a
        // live-arriving load at the top by its own timestamp instead of by the accident of when it was pushed.
        // Every branch goes through `sortByNumericKey`, so all four share one null rule and one direction
        // convention — and none of them re-parses a timestamp per comparison.
        if (!sort) return sortByNewestFirst(loads)
        // Unknown arrival sorts last in BOTH directions — same rule as the default order. A load whose
        // timestamp tells us nothing must not win the top row just because the operator flipped the arrow.
        if (sort.col === 'received') return sortByNumericKey(loads, arrivedAt, sort.dir)
        if (sort.col === 'miles') return sortByNumericKey(loads, milesValue, sort.dir)
        // Narrowed to a shared SortColumn (miles/received handled above) — capture so the closure keeps the
        // narrowing. Same values `compareLoadsBy` ranks by, read once per load instead of once per comparison.
        const col = sort.col
        return sortByNumericKey(loads, (load) => loadSortValue(load, col), sort.dir)
    }, [loads, sort])

    // A live load prepended while the operator is at the top must be visible there, not just present —
    // see the hook.
    const { tableRef, atTopStateChange } = useKeepBoardAtTop(sortedLoads)

    // Virtuoso re-renders every visible row when `context` changes identity, so a fresh object per render
    // would have made the memoized cells below pointless. Every field is in the dep list; `viewedKeys` is a
    // stable snapshot from an external store, and the overlay records come from the caller unchanged.
    const context: RowContext = useMemo(
        () => ({
            selectedKey,
            currentOperatorId,
            operatorBids,
            bids,
            viewedKeys,
            onRowClick: handleRowClick,
            onOpenNewTab: handleOpenNewTab,
            minTableWidth,
        }),
        [selectedKey, currentOperatorId, operatorBids, bids, viewedKeys, handleRowClick, handleOpenNewTab, minTableWidth]
    )

    const renderHeader = useCallback(
        () => (
            <tr className="border-b border-border bg-background">
                {shownColumns.map((col) => (
                    <th
                        key={col.id}
                        className={`whitespace-nowrap bg-background ${col.narrow ? NARROW_PAD : WIDE_PAD} py-2 ${col.align === 'center' ? 'text-center' : 'text-left'} text-[11px] font-semibold uppercase tracking-wider text-foreground-muted`}
                    >
                        <span className={`inline-flex items-center ${col.align === 'center' ? 'w-full justify-center' : ''}`}>
                            {col.label}
                            {col.sort && <FilterIndicator sort={dirFor(col.sort)} onSort={(next) => setColSort(col.sort!, next)} />}
                        </span>
                    </th>
                ))}
            </tr>
        ),
        [shownColumns, dirFor, setColSort]
    )

    const renderRow = useCallback(
        (_index: number, load: CanonicalLoad) => {
            const key = loadKey(load)
            return (
                <BoxTruckRowCells
                    load={load}
                    columns={shownColumns}
                    bids={bids[key] ?? NO_BIDS}
                    bidders={bidders[key] ?? NO_BIDDERS}
                    operatorBids={operatorBids[key] ?? NO_OPERATOR_BIDS}
                    annotation={annotations[key]}
                    thread={emailThreads[key]}
                    awaitingDesk={awaitingDesk.has(key)}
                    onRate={rateHandler}
                    onOpen={handleRowClick}
                />
            )
        },
        [shownColumns, bids, bidders, operatorBids, annotations, emailThreads, awaitingDesk, rateHandler, handleRowClick]
    )

    // Vertically virtualized (only the visible rows are in the DOM) so the board can hold the whole active-load
    // feed without growing thousands of <tr> nodes; the header stays sticky and horizontal scroll is preserved.
    return (
        <TableVirtuoso
            ref={tableRef}
            style={FILL_HEIGHT}
            data={sortedLoads}
            context={context}
            computeItemKey={itemKey}
            scrollerRef={captureScroller}
            endReached={endReached}
            isScrolling={isScrolling}
            atBottomStateChange={atBottomStateChange}
            atBottomThreshold={VIRTUOSO_OVERSCAN_PX}
            atTopStateChange={atTopStateChange}
            atTopThreshold={AT_TOP_THRESHOLD_PX}
            increaseViewportBy={{ top: VIRTUOSO_OVERSCAN_PX, bottom: VIRTUOSO_OVERSCAN_PX }}
            components={{ Table: VirtuosoTable, Scroller: VirtuosoScroller, TableHead: VirtuosoTableHead, TableRow: VirtuosoTableRow }}
            fixedHeaderContent={renderHeader}
            itemContent={renderRow}
        />
    )
}
